<script lang="ts">
  import type { UnitType } from "api/site_maps"
  import { getContext, onDestroy, onMount } from "svelte"
  import { scaleLinear } from "d3-scale"
  import { isMobile, toRadians } from "./utils"

  export let storageUnit: { id: string; typeId: string; name: string }
  export let storageUnitRowSelected: boolean
  export let storageUnitTypesById: Map<string, any>
  export let x: number
  export let y: number

  const customerPopoverStore = getContext("customerPopoverStore")
  const dialogStore = getContext("dialogStore")
  const mapSettings = getContext("mapSettings")
  const statusesStore = getContext("sitemapStatuses")
  const { xScale, yScale } = getContext("LayerCake")

  let shape
  let text
  let unitStatuses: { [key: string]: UnitType | undefined } = {}
  let unitStatusesUnsubscribe = () => {}

  if (statusesStore) {
    // @ts-ignore
    unitStatusesUnsubscribe = statusesStore.subscribe((value) => {
      unitStatuses = value
    })
  }

  function getDimensions({ cos, element, sin }) {
    if (!element) return { height: 0, width: 0 }

    const bBox = element.getBBox()
    const height = bBox.height
    const width = bBox.width

    return {
      height: height * cos + width * sin,
      width: width * cos + height * sin,
    }
  }

  function getStyleClasses($statuses) {
    const unitStatus = $statuses[storageUnit.id] || {}

    const classes = {
      shape: "stroke-black fill-white stroke group-hover:fill-gray-300 cursor-pointer",
      text: "fill-black cursor-pointer",
    }

    if (unitStatus.disabled) {
      classes.shape =
        "transition-colors text-white fill-gray-900/80 stroke-black group-hover:fill-black/60 cursor-pointer"
      classes.text = "fill-white font-bold cursor-pointer"
    }

    if (unitStatus.occupancyStatus === "occupied") {
      classes.shape =
        "transition-colors fill-green-300 stroke-green-800 group-hover:fill-green-500 cursor-pointer"
      classes.text = "fill-green-800 font-bold cursor-pointer"
    }

    if (unitStatus.occupancyStatus === "past_due") {
      classes.shape =
        "transition-colors fill-red-300 stroke-4 stroke-red-800 group-hover:fill-red-400 cursor-pointer"
      classes.text = "fill-red-800 font-bold cursor-pointer"
    }

    if (unitStatus.scheduledMoveIn) {
      classes.shape =
        "transition-colors fill-blue-300 stroke-blue-800 group-hover:fill-blue-500 cursor-pointer"
      classes.text = "fill-blue-800 font-bold cursor-pointer"
    }

    if (unitStatus.scheduledMoveOut) {
      classes.shape =
        "transition-colors fill-purple-300 stroke-4 stroke-purple-800 group-hover:fill-purple-400 cursor-pointer"
      classes.text = "fill-purple-700 font-bold cursor-pointer"
    }

    if (unitStatus.locked) {
      classes.shape =
        "transition-colors fill-cyan-300 stroke-4 stroke-cyan-700 group-hover:fill-cyan-400 cursor-pointer"
      classes.text = "fill-cyan-700 fill-cyan-700 font-bold cursor-pointer"
    }

    if (unitStatus.auction) {
      classes.shape =
        "transition-colors fill-yellow-300 stroke-4 stroke-yellow-700 group-hover:fill-yellow-400 cursor-pointer"
      classes.text = "fill-yellow-700 fill-white font-bold cursor-pointer"
    }

    if ($mapSettings.isEditing) {
      classes.shape =
        "transition-colors stroke-black fill-sky-100 stroke group-hover:fill-sky-200 cursor-pointer"
    }

    return classes
  }

  function getTextGeometry($storageUnitType) {
    const rotationRadians = toRadians($storageUnitType.nameRotation)
    const absCos = Math.abs(Math.cos(rotationRadians))
    const absSin = Math.abs(Math.sin(rotationRadians))

    return { absCos, absSin }
  }

  function handleClick() {
    if ($mapSettings.isEditing) return

    dialogStore.set({ isOpen: true, storageUnitId: storageUnit.id })
  }

  function handleMouseMove(id) {
    return (e) => {
      if (isMobile()) return
      if (!shape) return

      customerPopoverStore?.update((current) => ({
        ...current,
        id,
        storageUnitRect: shape.getBoundingClientRect(),
      }))
    }
  }

  function setTextContent({
    storageUnit: $storageUnit,
    textGeometry: textGeometry,
    xscale: $xscale,
  }) {
    if (!shape) return
    if (!text) return

    const fullText = $storageUnit.name
    text.textContent = fullText

    const { absCos: absCosText, absSin: absSinText } = textGeometry

    const shapeHeight = shape.getBBox().height
    const shapeWidth = shape.getBBox().width

    const xPadding = shapeWidth * 0.2
    const yPadding = shapeHeight * 0.2

    const { height: textHeight, width: textWidth } = getDimensions({
      cos: absCosText,
      element: text,
      sin: absSinText,
    })

    if (textHeight + yPadding > shapeHeight || textWidth + xPadding > shapeWidth) {
      let textDimensions = getDimensions({ cos: absCosText, element: text, sin: absSinText })
      let textLength = text.textContent.length

      while (
        (textDimensions.width + xPadding > shapeWidth ||
          textDimensions.height + yPadding > shapeHeight) &&
        textLength > 0
      ) {
        let truncatedText = fullText.slice(0, --textLength)
        truncatedText = truncatedText.length ? `${truncatedText}...` : ""

        text.textContent = truncatedText
        textDimensions = getDimensions({ cos: absCosText, element: text, sin: absSinText })
      }
    }
  }

  $: styleClasses = getStyleClasses(unitStatuses)
  $: xScale0 = $xScale(0)

  $: localXScale = scaleLinear().range([0, $xScale(1) - xScale0])
  $: localYScale = scaleLinear().range([0, $yScale(1) - $yScale(0)])

  $: storageUnitType = storageUnitTypesById.get(storageUnit.typeId)

  $: polygonPoints = storageUnitType.shape
    .map((p) => `${localXScale(x + p[0])},${localYScale(y + p[1])}`)
    .join(" ")

  $: textGeometry = getTextGeometry(storageUnitType)

  $: {
    requestAnimationFrame(() => {
      setTextContent({ storageUnit, textGeometry, xScale0 })
    })
  }

  $: textTransformX = localXScale(x + storageUnitType.namePosition.x)
  $: textTransformY = localYScale(y + storageUnitType.namePosition.y)

  onDestroy(unitStatusesUnsubscribe)

  onMount(() => {
    setTextContent({ storageUnit, textGeometry, xScale0 })
  })
</script>

<g
  class="focus:outline-none group"
  on:click={handleClick}
  on:keypress={(e) => {
    if (e.key === "Enter") {
      handleClick(e)
    }
  }}
  on:mouseenter={handleMouseMove(storageUnit.id)}
  on:mouseleave={handleMouseMove("")}
  role="button"
  tabindex="-1"
>
  <polygon
    bind:this={shape}
    class={`${styleClasses.shape} focus:ring-transparent ${
      storageUnitRowSelected ? "stroke-blue-500 stroke-2" : `stroke-black`
    }`}
    points={polygonPoints}
  />

  <text
    bind:this={text}
    class={styleClasses.text + " focus:outline-none z-0 transition-opacity duration-500"}
    dominant-baseline="middle"
    role="button"
    style={`font-size: ${$mapSettings.storageUnitTextFontSizeRem}rem`}
    tabindex="-1"
    text-anchor="middle"
    transform={`rotate(${storageUnitType.nameRotation}, ${textTransformX}, ${textTransformY})`}
    x={textTransformX}
    y={textTransformY}
  />
</g>
